import React, { useState, useEffect, useMemo } from 'react'
import { useTimer, useTimerElapsed } from '../../contexts/TimerContext'
import { useAuth } from '../../contexts/AuthContext'
import { isDepartureReadOnly } from '../../utils/departureStatus'
import TimerDisplay from '../../components/Timer/TimerDisplay'
import TimerOperationCard from '../../components/Timer/TimerOperationCard'
import TimeEntryList from '../../components/Timer/TimeEntryList'
import ManualTimeEntryModal, { ManualTimeEntryData } from '../../components/Timer/ManualTimeEntryModal'
import EditTimeEntryModal, { EditTimeEntryData } from '../../components/Timer/EditTimeEntryModal'
import WeekLockStatusBanner from '../../components/Timer/WeekLockStatusBanner'
import TimerLockBanner from '../../components/Timer/TimerLockBanner'
import OnboardingChecklist from '../../components/Onboarding/OnboardingChecklist'
import FeatureTourModal from '../../components/Onboarding/FeatureTourModal'
import { onboardingService } from '../../services/onboardingService'
import { TimeEntry, ViewMode, OnboardingProgress } from '../../types'
import { AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'

// 入職引導的本機狀態（每位使用者分開記，避免同一台電腦換人登入時互相影響）
const onboardingDismissKey = (userId: string) => `anytime.onboarding.dismissed.${userId}`
const tourSeenKey = (userId: string) => `anytime.tour.seen.${userId}`

// 從 TimerOperationCard 導入 RecentTask 介面
import type { RecentTask } from '../../components/Timer/TimerOperationCard'

const TimerPage: React.FC = () => {
  const [isManualModalOpen, setIsManualModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)

  // 檢視模式狀態
  const [viewMode, setViewMode] = useState<ViewMode>('today')

  const { user } = useAuth()
  const elapsedTime = useTimerElapsed()
  const {
    // 計時器狀態
    isRunning,

    // 專案相關
    projects,
    selectedProject,
    recentFavoriteProjects,
    selectProject,
    
    // 工作描述
    description,
    setDescription,
    
    // 今日記錄
    todayEntries,
    totalTodayDuration,

    // 過去 7 天記錄（同時餵給近期任務建議與週檢視）
    weeklyEntries,
    weeklyTotalDuration,
    isWeeklyLoaded,
    loadWeeklyEntries,

    // 週次鎖定狀態
    weekLockStatus,
    isCheckingWeekLock,

    // 統一 Gate 鎖定狀態（過期文件 / 必讀公告 / 個資未填…）
    gates,
    isCheckingGates,
    refreshGates,

    // 載入狀態
    isLoading,
    error,

    // 動作函數
    startTimer,
    quickStartTimer,
    stopTimer,
    deleteTimeEntry,
    createManualTimeEntry,
    editTimeEntry,
    checkCurrentWeekLock,
    loadProjects
  } = useTimer()

  // ===== 新夥伴入職引導 =====
  const [onboarding, setOnboarding] = useState<OnboardingProgress | null>(null)
  const [onboardingDismissed, setOnboardingDismissed] = useState(true)
  const [isTourOpen, setIsTourOpen] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    const userId = user.id

    try {
      setOnboardingDismissed(localStorage.getItem(onboardingDismissKey(userId)) === '1')
    } catch {
      setOnboardingDismissed(false)
    }

    let cancelled = false
    onboardingService
      .getOnboardingProgress()
      .then(progress => {
        if (cancelled) return
        setOnboarding(progress)

        // 還沒走完入職流程的新夥伴，第一次進來自動開一次功能導覽
        if (!progress.completed) {
          try {
            if (localStorage.getItem(tourSeenKey(userId)) !== '1') {
              setIsTourOpen(true)
              localStorage.setItem(tourSeenKey(userId), '1')
            }
          } catch {
            // localStorage 不可用（無痕模式等）→ 就不自動開，不影響其他功能
          }
        }
      })
      .catch(() => {
        // 引導只是輔助，抓不到就安靜地不顯示
      })

    return () => {
      cancelled = true
    }
  }, [user?.id])

  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true)
    if (!user?.id) return
    try {
      localStorage.setItem(onboardingDismissKey(user.id), '1')
    } catch {
      // 存不進去就只在這次 session 隱藏
    }
  }

  // 完成後就不再顯示，使用者手動關掉也不再顯示
  const showOnboarding = !!onboarding && !onboarding.completed && !onboardingDismissed

  // 處理開始計時
  const handleStartTimer = async () => {
    try {
      await startTimer()
      toast.success('計時已開始')
    } catch (error) {
      toast.error('開始計時失敗')
    }
  }

  // 處理停止計時
  const handleStopTimer = async () => {
    try {
      await stopTimer()
      toast.success('計時已停止')
    } catch (error) {
      toast.error('停止計時失敗')
    }
  }

  // 處理編輯計時記錄
  const handleEditEntry = (entry: TimeEntry, originalDate?: string) => {
    if (entry.status === 'RUNNING') {
      toast.error('無法編輯進行中的計時記錄')
      return
    }
    
    setEditingEntry({
      ...entry,
      originalDate
    } as TimeEntry & { originalDate?: string })
    setIsEditModalOpen(true)
  }

  // 處理編輯提交
  const handleEditSubmit = async (data: EditTimeEntryData) => {
    try {
      const editData = {
        ...data,
        originalDate: (editingEntry as TimeEntry & { originalDate?: string })?.originalDate
      }
      
      await editTimeEntry(editData)
      setIsEditModalOpen(false)
      setEditingEntry(null)
      
      if (viewMode === 'week') {
        await loadWeeklyEntries()
      }
    } catch (error) {
      // Error handled in modal
    }
  }

  // 處理編輯取消
  const handleEditCancel = () => {
    setIsEditModalOpen(false)
    setEditingEntry(null)
  }

  // 處理刪除計時記錄
  const handleDeleteEntry = async (entryId: string) => {
    if (window.confirm('確定要刪除這筆計時記錄嗎？')) {
      try {
        await deleteTimeEntry(entryId)
        toast.success('記錄已刪除')
      } catch (error) {
        toast.error('刪除記錄失敗')
      }
    }
  }

  // 處理手動新增時間記錄
  const handleManualAdd = () => {
    setIsManualModalOpen(true)
  }

  // 處理手動新增提交
  const handleManualSubmit = async (data: ManualTimeEntryData) => {
    try {
      await createManualTimeEntry(data)
      setIsManualModalOpen(false)
    } catch (error) {
      // Error handled in modal
    }
  }

  // 處理手動新增取消
  const handleManualCancel = () => {
    setIsManualModalOpen(false)
  }

  // 處理重新載入專案
  const handleReloadProjects = async () => {
    try {
      await loadProjects(true)
      toast.success('專案列表已更新')
    } catch (error) {
      toast.error('重新載入專案失敗')
    }
  }

  // 處理檢視模式變更
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
  }

  // 切到週檢視時刷新（首次資料已由 TimerContext bootstrap 載入）
  useEffect(() => {
    if (viewMode === 'week') {
      loadWeeklyEntries()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode])

  // 註：這裡曾加過一個「掛載時若 isWeeklyLoaded 為 false 就補打 loadWeeklyEntries」
  // 的 effect，已移除。原因是它與 TimerProvider 的 loadTimerBootstrap 並行送出——
  // 兩者 request key 不同，去重層擋不住，而 bootstrap 本來就會回傳同一份週資料，
  // 等於每次進站多一次 DB 查詢，兩個回應還可能亂序覆蓋 weeklyEntries。
  // 真正的成因（被 abort 的請求佔住去重冷卻時間）已在 TimerContext 修掉，
  // 使用者切到週檢視時上面那個 effect 就會正常重新請求，不需要額外補救。

  // 近期任務記錄 — 從 weekly + 今日合併去重（兩者來源都在 TimerContext）
  const recentEntries = useMemo<TimeEntry[]>(() => {
    const merged: TimeEntry[] = []
    const seen = new Set<string>()
    for (const entry of todayEntries) {
      if (!seen.has(entry.id)) {
        merged.push(entry)
        seen.add(entry.id)
      }
    }
    for (const dateKey of Object.keys(weeklyEntries)) {
      for (const entry of weeklyEntries[dateKey].entries) {
        if (!seen.has(entry.id)) {
          merged.push(entry)
          seen.add(entry.id)
        }
      }
    }
    return merged
  }, [todayEntries, weeklyEntries])

  // 離職唯讀模式
  const isReadOnly = isDepartureReadOnly(user?.departureDate)

  // 檢查是否可以進行操作（gate 鎖定、週次鎖定、離職唯讀都會阻擋）
  const canOperate = gates.length === 0 && !weekLockStatus?.isLocked && !isReadOnly

  // 從近期記錄（過去 7 天）中提取不重複的任務
  const recentTasks = useMemo((): RecentTask[] => {
    const entriesWithDescription = recentEntries
      .filter(entry => entry.description && entry.description.trim() !== '' && entry.status !== 'RUNNING')
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())

    const uniqueTasksMap = new Map<string, RecentTask>()

    for (const entry of entriesWithDescription) {
      const key = `${entry.description}-${entry.projectId}`

      if (!uniqueTasksMap.has(key)) {
        uniqueTasksMap.set(key, {
          description: entry.description!,
          projectId: entry.projectId,
          projectName: entry.project?.name || '未知專案'
        })
      }

      if (uniqueTasksMap.size >= 6) {
        break
      }
    }

    return Array.from(uniqueTasksMap.values())
  }, [recentEntries])

  // 處理快速開始任務
  const handleQuickStart = async (task: RecentTask) => {
    const project = projects.find(p => p.id === task.projectId)
    
    if (project) {
      try {
        await quickStartTimer(project, task.description)
        toast.success('計時已開始')
      } catch (error) {
        toast.error('開始計時失敗')
      }
    } else {
      setDescription(task.description)
      toast.error('找不到對應專案，請重新選擇')
    }
  }

  return (
    <div className="page-container space-y-6 animate-fade-in">
      {/* 錯誤提示 */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl animate-fade-in">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-red-100 
            flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* 新夥伴入職清單 —— 完成或關閉後不再顯示 */}
      {showOnboarding && onboarding && (
        <OnboardingChecklist
          progress={onboarding}
          onDismiss={handleDismissOnboarding}
          onOpenTour={() => setIsTourOpen(true)}
        />
      )}

      {/* Gate 鎖定橫幅 - 優先顯示（依後端排序取第一個未過關的 gate） */}
      {gates[0] ? (
        <TimerLockBanner
          gate={gates[0]}
          onRefresh={() => refreshGates(true)}
          isRefreshing={isCheckingGates}
        />
      ) : weekLockStatus && (
        /* 週次鎖定狀態橫幅 - 無 gate 鎖定時才顯示 */
        <WeekLockStatusBanner
          lockStatus={weekLockStatus}
          onRefresh={checkCurrentWeekLock}
          isRefreshing={isCheckingWeekLock}
        />
      )}

      {/* 主要內容區 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左側欄：計時器控制 */}
        <div className="space-y-6">
          {/* 計時器顯示 */}
          <div className="glass-card-elevated rounded-2xl md:rounded-3xl overflow-hidden">
            <TimerDisplay
              elapsedTime={elapsedTime}
              isRunning={isRunning}
            />
          </div>

          {/* 操作卡片 */}
          <TimerOperationCard
            isRunning={isRunning}
            isLoading={isLoading}
            description={description}
            onDescriptionChange={setDescription}
            projects={projects}
            selectedProject={selectedProject}
            recentFavoriteProjects={recentFavoriteProjects}
            onSelectProject={selectProject}
            onReloadProjects={handleReloadProjects}
            onStart={handleStartTimer}
            onStop={handleStopTimer}
            recentTasks={recentTasks}
            onQuickStart={handleQuickStart}
            disabled={!canOperate}
          />
        </div>

        {/* 右側欄：時間記錄列表 */}
        <div className="glass-card rounded-2xl md:rounded-3xl p-5 md:p-8">
          <TimeEntryList
            entries={todayEntries}
            totalDuration={totalTodayDuration}
            weeklyEntries={weeklyEntries}
            weeklyTotalDuration={weeklyTotalDuration}
            isWeeklyLoaded={isWeeklyLoaded}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onEdit={handleEditEntry}
            onDelete={handleDeleteEntry}
            onStop={handleStopTimer}
            onManualAdd={handleManualAdd}
            disabled={!canOperate}
          />
        </div>
      </div>

      {/* 手動新增時間記錄 Modal */}
      <ManualTimeEntryModal
        isOpen={isManualModalOpen}
        onClose={handleManualCancel}
        onSubmit={handleManualSubmit}
        projects={projects}
        isLoading={isLoading}
      />

      {/* 編輯時間記錄 Modal */}
      <EditTimeEntryModal
        isOpen={isEditModalOpen}
        entry={editingEntry}
        onClose={handleEditCancel}
        onSubmit={handleEditSubmit}
        projects={projects}
        isLoading={isLoading}
      />

      {/* 功能導覽：新夥伴首次登入自動開啟一次，之後可從入職清單再開 */}
      <FeatureTourModal isOpen={isTourOpen} onClose={() => setIsTourOpen(false)} />
    </div>
  )
}

export default TimerPage
